import { EvidenceReportToggle } from "../../components/task/EvidenceReportToggle";
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Camera, ChevronDown, Cross, Eye, Smartphone, Upload, X as XIcon, MapPin, Globe, Lightbulb, Sparkles, ShieldCheck, RotateCcw } from "lucide-react";
import { CreateTaskMap } from "../../components/task/CreateTaskMap";
import { B2BCreateSection } from "../../components/b2b/B2BCreateSection";
import { getCurrentLocation, geocodeAddressClientSide } from "../../utils/maps";
import { GENERAL_SUBCATEGORIES, RESULT_TYPE_INFO, GENERAL_PRESETS, getSubcategory } from "../../utils/generalSubcategories";
import { MIN_PAID_TASK_PRICE, MIN_PAID_TASK_PRICE_ERROR } from "../../utils/taskCancel";

// Подсказки-примеры для AI-помощника и полей формы по типу услуги
const AI_PLACEHOLDERS = {
  general: "Например: починить капающий кран на кухне в Краснодаре",
  secret_shopper: "Например: проверить кафе на улице Красной — вежливость персонала, чистоту зала и скорость обслуживания",
  grave_care: "Например: убрать участок, покрасить оградку и возложить цветы на Славянском кладбище",
  mobile_journalist: "Например: снять фото и видео последствий непогоды в Юбилейном микрорайоне",
};
const TITLE_PLACEHOLDERS = {
  general: "Например: Фото парка Горького",
  secret_shopper: "Например: Проверка кафе на улице Красной",
  grave_care: "Например: Уборка участка на Славянском кладбище",
  mobile_journalist: "Например: Фоторепортаж с городского события",
};
const DESC_PLACEHOLDERS = {
  secret_shopper: "Опишите точку проверки: адрес, время визита, легенда, что оценить, нужен ли чек...",
  grave_care: "Опишите местоположение участка: кладбище, сектор, ориентиры. Что нужно сделать и как принять результат...",
  mobile_journalist: "Опишите событие: что происходит, где, какие кадры нужны, ракурсы, сроки сдачи материала...",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CreateTask = ({ user, setCurrentView, showToast, defaultServiceType = "general", defaultSubcategory = "photo_video" }) => {
  // TZ-9 п.5: плашки главной предвыбирают подкатегорию «Общих услуг» (пустая строка — без подкатегории)
  const initialSub = getSubcategory(defaultSubcategory);
  // Администратор может создавать задания независимо от общего гейта paid_tasks_enabled
  // (совпадает с backend-логикой в create_task: current_user.role != UserRole.ADMIN).
  const isAdmin = user?.role === "admin";
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    latitude: 55.7558,
    longitude: 37.6173,
    price: "",
    deadline: "",
    service_type: defaultServiceType,
    urgency_level: "low",
    verified_journalists_only: false,
    verified_executors_only: false,
    media_content_type: "photo_video",
    task_subcategory: defaultSubcategory,
    result_type: initialSub ? initialSub.result_type : "media",
    location_required: initialSub ? initialSub.location_required : true,
    reference_media: [],
    is_b2b: false,
    b2b_inn: "",
    b2b_org_name: "",
    b2b_docs_email: "",
    evidence_photo_report: false
  });
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [mapInstance, setMapInstance] = useState(null);
  // Чеклист заказчика для мемориального сервиса
  const GRAVE_CARE_WORKS = [
    { id: 1,  label: "Удаление сорняков и травы по периметру и внутри ограды" },
    { id: 2,  label: "Уборка мусора, листвы и веток" },
    { id: 3,  label: "Мытьё надгробного памятника" },
    { id: 4,  label: "Мытьё и чистка ограды" },
    { id: 5,  label: "Покраска ограды" },
    { id: 6,  label: "Покраска лавочки и стола" },
    { id: 7,  label: "Восстановление или укладка тротуарной плитки" },
    { id: 8,  label: "Посадка или полив цветов" },
    { id: 9,  label: "Замена живых или искусственных цветов" },
    { id: 10, label: "Замена лампадки или свечей" },
    { id: 11, label: "Фотофиксация повреждений для будущего ремонта" },
    { id: 12, label: "Общая фотофиксация участка (до и после)" },
  ];
  const [selectedWorks, setSelectedWorks] = useState([]);
  const [worksCollapsed, setWorksCollapsed] = useState(false);
  const [serviceTypes, setServiceTypes] = useState({
    general: { name: "Общие услуги", commission: 10, description: "Любые поручения поблизости: фото/видео, бытовой ремонт, помощь по дому, покупки и доставка, поиск информации, документы и курсовые", requirements: [] },
    secret_shopper: { name: "Тайный покупатель", commission: 15, description: "Анонимная оценка качества обслуживания", requirements: ["anonymous_performer", "template_report", "mandatory_photo_proof"] },
    grave_care: { name: "Мемориальный сервис", commission: 15, description: "Услуги по уходу за местом захоронения", checklist: [], subscription: true },
    mobile_journalist: { name: "Мобильный корреспондент", commission: 25, description: "Срочные задания для СМИ", requirements: [] }
  });
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [externalLinks, setExternalLinks] = useState([]);
  // Пользовательские пункты проверки для тайного покупателя
  const [shopperCustomFields, setShopperCustomFields] = useState([]);
  const [newShopperField, setNewShopperField] = useState('');
  // Аудит #35: прозрачный расчёт комиссии
  const [commissionPreview, setCommissionPreview] = useState(null);
  // Гейт запуска: разрешена ли сейчас публикация платных заданий (false до загрузки конфига — безопасный дефолт)
  const [paidTasksEnabled, setPaidTasksEnabled] = useState(false);
  // AI-помощник может быть отключён администратором (например, при низком балансе)
  const [aiHelperEnabled, setAiHelperEnabled] = useState(false);
  // AI-помощник формулировки задания
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiHelper, setShowAiHelper] = useState(false);
  // Сворачиваемые блоки формы "Общие услуги"
  const [subcatCollapsed, setSubcatCollapsed] = useState(false);
  const [presetsExpanded, setPresetsExpanded] = useState(false);

  // Выбор подкатегории "Общих услуг" — настраивает тип результата и обязательность локации
  const selectSubcategory = (value) => {
    const sub = getSubcategory(value);
    if (!sub) return;
    setFormData(prev => ({
      ...prev,
      task_subcategory: sub.value,
      result_type: sub.result_type,
      location_required: sub.location_required,
    }));
  };

  // Применить готовый шаблон-пресет
  const applyPreset = (preset) => {
    const sub = getSubcategory(preset.subcategory);
    setFormData(prev => ({
      ...prev,
      title: preset.title,
      description: preset.description,
      task_subcategory: preset.subcategory,
      result_type: sub ? sub.result_type : prev.result_type,
      location_required: sub ? sub.location_required : prev.location_required,
    }));
    if (showToast) showToast("Шаблон применён — отредактируйте детали под себя", "success");
  };

  // AI-помощник: сформулировать название и описание из короткого ввода
  const runAiSuggest = async () => {
    const text = aiPrompt.trim();
    if (text.length < 3) {
      if (showToast) showToast("Опишите задание хотя бы парой слов", "info");
      return;
    }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/tasks/ai-suggest`, {
        prompt: text,
        subcategory: formData.service_type === "general" ? formData.task_subcategory : null,
        service_type: formData.service_type,
      });
      setFormData(prev => ({
        ...prev,
        title: res.data.title || prev.title,
        description: res.data.description || prev.description,
      }));
      if (showToast) showToast("Задание сформулировано — проверьте и отредактируйте при необходимости", "success");
    } catch (error) {
      const msg = error.response?.data?.detail || "Не удалось сформулировать задание. Попробуйте ещё раз.";
      if (showToast) showToast(msg, "error");
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    axios.get(`${API}/public/site-config`)
      .then(res => { if (active) { setPaidTasksEnabled(!!res.data.paid_tasks_enabled); setAiHelperEnabled(res.data.ai_helper_enabled !== false); } })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    // Scroll to top when task creation form opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Try to get user's current location
    getCurrentUserLocation();
    // Load service types
    loadServiceTypes();
  }, []);

  // Аудит #35: пересчёт комиссии при изменении цены или типа услуги (с дебаунсом)
  useEffect(() => {
    const priceNum = parseFloat(formData.price);
    if (!priceNum || priceNum < MIN_PAID_TASK_PRICE || priceNum > 50000) {
      setCommissionPreview(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/commission/preview`, {
          params: { service_type: formData.service_type, price: priceNum },
        });
        setCommissionPreview(response.data);
      } catch (error) {
        // Тихая ошибка — расчёт нужен для отображения, не блокирует создание
        setCommissionPreview(null);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [formData.price, formData.service_type]);

  // Блокируем скролл страницы при полноэкранной карте
  useEffect(() => {
    if (mapFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mapFullscreen]);

  const loadServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/service-types`);
      setServiceTypes(response.data);
    } catch (error) {
      console.error("Error loading service types:", error);
    }
  };

  const getCurrentUserLocation = async () => {
    try {
      const location = await getCurrentLocation();
      
      // Обновляем координаты
      setFormData(prev => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude
      }));
      
      // Пытаемся получить адрес по координатам
      try {
        const response = await axios.get(`${API}/location/reverse-geocode?latitude=${location.latitude}&longitude=${location.longitude}`);
        setFormData(prev => ({
          ...prev,
          latitude: location.latitude,
          longitude: location.longitude,
          location: response.data.address
        }));
        if (showToast) {
          showToast("Местоположение и адрес определены автоматически", "success");
        }
      } catch (error) {
        console.error("Reverse geocoding error:", error);
        if (showToast) {
          showToast("Координаты определены, но адрес не найден", "info");
        }
      }
    } catch (error) {
      console.error("Could not get user location:", error);
      
      // Fallback: используем координаты по умолчанию (Москва) и получаем адрес
      try {
        const response = await axios.get(`${API}/location/reverse-geocode?latitude=55.7558&longitude=37.6173`);
        setFormData(prev => ({
          ...prev,
          latitude: 55.7558,
          longitude: 37.6173,
          location: response.data.address
        }));
        if (showToast) {
          showToast("Использованы координаты по умолчанию (Москва)", "info");
        }
      } catch (fallbackError) {
        console.error("Fallback geocoding error:", fallbackError);
        if (showToast) {
          showToast("Не удалось определить местоположение", "error");
        }
      }
    }
  };

  const geocodeAddress = async () => {
    const loc = formData.location.trim();
    if (!loc) {
      if (showToast) showToast("Введите адрес в поле для поиска", "info");
      return;
    }

    // Если поле содержит "Координаты: X, Y" — просто обновить карту без запроса к API
    const coordMatch = loc.match(/координаты[:\s]+([0-9.,-]+)[,\s]+([0-9.,-]+)/i)
      || loc.match(/^([0-9]{2,3}[.,][0-9]+)[,\s]+([0-9]{2,3}[.,][0-9]+)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1].replace(',', '.'));
      const lng = parseFloat(coordMatch[2].replace(',', '.'));
      if (!isNaN(lat) && !isNaN(lng)) {
        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
        if (showToast) showToast("Координаты применены на карте", "success");
        setShowMap(true);
      }
      return;
    }

    // Иначе — геокодировать текстовый адрес
    setLocationLoading(true);
    try {
      const response = await axios.get(`${API}/location/geocode?address=${encodeURIComponent(loc)}`);
      setFormData(prev => ({
        ...prev,
        latitude: response.data.latitude,
        longitude: response.data.longitude
      }));
      if (showToast) showToast("Местоположение найдено на карте", "success");
      setShowMap(true);
    } catch (error) {
      console.error("Geocoding error (backend Nominatim):", error);
      // Резервный вариант: если внешний сервис геокодирования на backend недоступен
      // (например, сетевые ограничения на хостинге), пробуем определить адрес
      // через Яндекс.Карты прямо в браузере пользователя.
      try {
        const coords = await geocodeAddressClientSide(loc);
        setFormData(prev => ({ ...prev, latitude: coords.latitude, longitude: coords.longitude }));
        if (showToast) showToast("Местоположение найдено на карте", "success");
        setShowMap(true);
      } catch (fallbackError) {
        console.error("Geocoding error (Yandex fallback):", fallbackError);
        const msg = error.response?.data?.detail || "Не удалось найти адрес. Попробуйте ещё раз или уточните адрес.";
        if (showToast) showToast(msg, "error");
      }
    } finally {
      setLocationLoading(false);
    }
  };

  // Handle file upload for reference media
  const handleReferenceMediaUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Check file sizes (max 5MB per file)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    const oversizedFiles = files.filter(file => file.size > maxSize);
    
    if (oversizedFiles.length > 0) {
      showToast(`Файлы слишком большие (макс. 5MB): ${oversizedFiles.map(f => f.name).join(', ')}`, "error");
      return;
    }
    
    // Check total files count (general — до 5, прочие — до 3)
    const refLimit = formData.service_type === "general" ? 5 : 3;
    if (formData.reference_media.length + files.length > refLimit) {
      showToast(`Максимум ${refLimit} ${refLimit === 5 ? 'фотографий' : 'файла подсказок'}`, "error");
      return;
    }
    
    setUploadingFiles(true);
    
    Promise.all(files.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    })).then(base64Files => {
      setFormData(prev => ({
        ...prev,
        reference_media: [...prev.reference_media, ...base64Files]
      }));
      setUploadingFiles(false);
      showToast(`Загружено файлов: ${files.length}`, "success");
    }).catch(error => {
      console.error('Error uploading files:', error);
      setUploadingFiles(false);
      showToast("Ошибка при загрузке файлов", "error");
    });
  };

  // Handle file upload via button click
  const handleReferenceMediaButtonClick = () => {
    const input = document.getElementById('reference-media-input');
    if (input) {
      input.click();
    }
  };

  // Remove reference media
  const removeReferenceMedia = (index) => {
    setFormData(prev => ({
      ...prev,
      reference_media: prev.reference_media.filter((_, i) => i !== index)
    }));
  };

  // Add external link
  const addExternalLink = () => {
    if (!linkUrl.trim()) {
      showToast("Введите ссылку на файл", "error");
      return;
    }

    const newLink = {
      url: linkUrl.trim(),
      title: linkTitle.trim() || linkUrl.trim()
    };

    setExternalLinks(prev => [...prev, newLink]);
    setShowLinkModal(false);
    setLinkUrl('');
    setLinkTitle('');
    showToast("Ссылка добавлена!", "success");
  };

  // Remove external link
  const removeExternalLink = (index) => {
    setExternalLinks(prev => prev.filter((_, i) => i !== index));
    showToast("Ссылка удалена", "info");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paidTasksEnabled && !isAdmin) {
      showToast("Публикация заданий скоро откроется. Сейчас идёт набор исполнителей.", "info");
      return;
    }
    const priceVal = parseFloat(formData.price);
    if (priceVal > 50000) {
      showToast("Максимальная цена задания — 50 000 ₽", "error");
      return;
    }
    if (!formData.is_b2b && !(priceVal >= MIN_PAID_TASK_PRICE)) {
      showToast(MIN_PAID_TASK_PRICE_ERROR, "error");
      return;
    }
    if (formData.is_b2b) {
      const inn = (formData.b2b_inn || "").replace(/\D/g, "");
      if (inn.length !== 10 && inn.length !== 12) {
        showToast("Укажите ИНН организации: 10 или 12 цифр", "error");
        return;
      }
      if (!formData.b2b_org_name || !formData.b2b_org_name.trim()) {
        showToast("Укажите название организации", "error");
        return;
      }
      if (!formData.b2b_docs_email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.b2b_docs_email)) {
        showToast("Укажите корректный email для документов", "error");
        return;
      }
    }
    setLoading(true);

    try {
      let taskData = {
        ...formData,
        price: parseFloat(formData.price),
        deadline: new Date(formData.deadline).toISOString(),
        external_links: externalLinks
      };

      // Add checklist for grave_care tasks
      if (formData.service_type === "grave_care") {
        const allWorks = [
          { id: 1,  label: "Удаление сорняков и травы по периметру и внутри ограды" },
          { id: 2,  label: "Уборка мусора, листвы и веток" },
          { id: 3,  label: "Мытьё надгробного памятника" },
          { id: 4,  label: "Мытьё и чистка ограды" },
          { id: 5,  label: "Покраска ограды" },
          { id: 6,  label: "Покраска лавочки и стола" },
          { id: 7,  label: "Восстановление или укладка тротуарной плитки" },
          { id: 8,  label: "Посадка или полив цветов" },
          { id: 9,  label: "Замена живых или искусственных цветов" },
          { id: 10, label: "Замена лампадки или свечей" },
          { id: 11, label: "Фотофиксация повреждений для будущего ремонта" },
          { id: 12, label: "Общая фотофиксация участка (до и после)" },
        ];
        // Если заказчик отметил конкретные работы — берём только их, иначе весь список
        const worksToUse = selectedWorks.length > 0
          ? allWorks.filter(w => selectedWorks.includes(w.id))
          : allWorks;
        taskData.checklist = worksToUse.map(w => ({ ...w, completed: false }));
      }

      // Добавляем пользовательские пункты проверки для тайного покупателя
      if (formData.service_type === "secret_shopper" && shopperCustomFields.length > 0) {
        taskData.custom_shopper_fields = shopperCustomFields.filter(f => f.trim());
      }

      // Для не-журналистских заданий убираем поле типа контента
      if (formData.service_type !== "mobile_journalist") {
        delete taskData.media_content_type;
      }

      // Общие услуги (Уровень 2): нормализация подкатегории, типа результата и локации
      if (formData.service_type === "general") {
        const sub = getSubcategory(formData.task_subcategory);
        taskData.task_subcategory = formData.task_subcategory || "photo_video";
        taskData.result_type = sub ? sub.result_type : "media";
        taskData.location_required = sub ? sub.location_required : true;
        // Удалённое задание без адреса — подставляем понятную метку
        if (!taskData.location_required && (!taskData.location || !taskData.location.trim())) {
          taskData.location = "Удалённое задание";
        }
      } else {
        // Прочие типы услуг — стандартное поведение
        delete taskData.task_subcategory;
        taskData.evidence_photo_report = false;
        taskData.result_type = "media";
        taskData.location_required = true;
      }

      await axios.post(`${API}/tasks`, taskData);
      
      if (typeof window !== 'undefined' && window.ym) {
        window.ym(109617150, 'reachGoal', formData.is_b2b ? 'b2b_task_created' : 'task_created');
      }

      showToast(
        formData.is_b2b
          ? "Задание от организации опубликовано без оплаты: задание видно исполнителям с бизнес-статусом. Оплата — напрямую исполнителю."
          : "Задание создано! Перейдите в \"Мои задания\" и оплатите его, чтобы исполнители увидели задание.",
        "success", 10000
      );
      
      // Автоматически переводим пользователя во вкладку "Мои задания"
      setTimeout(() => {
        setCurrentView("my-tasks");
      }, 2000); // Даем время прочитать сообщение
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при создании задания", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>Создать задание</h2>

      {!formData.is_b2b && (
      <div data-testid="refund-guarantee-top" className="mb-6 p-4 rounded-xl flex items-start gap-3" style={{background:'rgba(13,148,136,0.06)', border:'1px solid rgba(13,148,136,0.25)'}}>
        <ShieldCheck size={20} className="mt-0.5 flex-shrink-0" style={{color:'var(--rv-teal)'}} />
        <div>
          <p className="font-semibold text-sm" style={{color:'var(--rv-teal)', fontFamily:'Manrope,sans-serif'}}>Деньги под защитой: передумали — вернём</p>
          <p className="text-sm mt-1" style={{color:'var(--rv-text-secondary)'}}>
            Оплата не уходит исполнителю сразу — она замораживается на защищённом счёте. Пока исполнитель не принял задание в работу, вы можете отменить его в один клик, и вся сумма вернётся на ту же карту. Исполнитель получает деньги только после того, как вы примете результат.
          </p>
        </div>
      </div>
      )}

      {!paidTasksEnabled && !isAdmin && (
        <div data-testid="paid-tasks-disabled-banner" className="mb-6 p-4 rounded-lg flex items-start gap-3" style={{background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.25)'}}>
          <Lightbulb size={18} className="mt-0.5 flex-shrink-0" style={{color:'#d97706'}} />
          <div>
            <p className="font-semibold text-sm" style={{color:'#92400e', fontFamily:'Manrope,sans-serif'}}>Приём заданий скоро откроется</p>
            <p className="text-sm mt-1" style={{color:'#b45309'}}>Сейчас идёт набор исполнителей в вашем городе. Вы уже зарегистрированы — мы сообщим, как только можно будет публиковать задания. Спасибо, что вы с нами на старте.</p>
          </div>
        </div>
      )}
      {!paidTasksEnabled && isAdmin && (
        <div data-testid="paid-tasks-disabled-admin-notice" className="mb-6 p-3 rounded-lg text-sm" style={{background:'rgba(13,148,136,0.06)', border:'1px solid rgba(13,148,136,0.25)', color:'var(--rv-teal)'}}>
          Публичный приём заданий сейчас выключен (набор исполнителей), но как администратор вы можете создавать задания для тестов и демонстрации.
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="reversia-card space-y-4">
        {/* Service Type Selection - First and Most Important */}
        <div className="rounded-xl p-4" style={{background:'var(--rv-paper)', border:'2px solid var(--rv-border)'}}>
          <label className="block text-lg font-semibold mb-3" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
            Выберите тип услуги в первую очередь
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(serviceTypes).map(([key, service]) => {
              const serviceInfo = {
                general: {
                  icon: <Camera size={22} className="text-blue-600" />,
                  title: "Общие услуги", 
                  description: "Любые поручения поблизости: фото и видео локаций, бытовой ремонт, помощь по дому, купить и доставить, найти информацию, составить документ или курсовую.",
                  image: "/images/service-types/general.jpg"
                },
                secret_shopper: {
                  icon: <Eye size={22} className="text-purple-600" />,
                  title: "Тайный покупатель",
                  description: "Проверка качества обслуживания в магазинах, ресторанах, отелях. Оценка персонала, ассортимента, чистоты с подробным отчетом.",
                  image: "/images/service-types/secret_shopper.jpg"
                },
                grave_care: {
                  icon: <Cross size={22} className="text-green-700" />,
                  title: "Мемориальный сервис",
                  description: "Уход за могилами близких: уборка, возложение цветов, фото состояния памятника. Деликатная помощь в сохранении памяти.",
                  image: "/images/service-types/grave_care.jpg"
                },
                mobile_journalist: {
                  icon: <Smartphone size={22} className="text-orange-500" />,
                  title: "Мобильный корреспондент", 
                  description: "Срочная журналистская съемка событий, новостей, происшествий. Требует верификации исполнителя и высокого качества контента.",
                  image: "/images/service-types/mobile_journalist.jpg"
                }
              };
              
              const info = serviceInfo[key] || { icon: <Camera size={22} />, title: service.name, description: service.description, image: null };
              const isActive = formData.service_type === key;
              
              return (
                <div
                  key={key}
                  onClick={() => {
                    if (key === "general") {
                      const sub = getSubcategory(formData.task_subcategory) || getSubcategory("photo_video");
                      setFormData({ ...formData, service_type: key, task_subcategory: sub.value, result_type: sub.result_type, location_required: sub.location_required });
                    } else {
                      setFormData({ ...formData, service_type: key, result_type: "media", location_required: true });
                    }
                  }}
                  className={`
                    service-type-card service-type-${key} 
                    border-2 rounded-lg cursor-pointer overflow-hidden
                    ${isActive ? 'active' : ''}
                    ${isActive ? '' : '!bg-white'}
                  `}
                  style={{
                    borderColor: isActive ? 'inherit' : '#e5e7eb',
                    backgroundColor: isActive ? 'inherit' : undefined
                  }}
                >
                  {/* Image */}
                  {info.image && (
                    <div className="w-full h-24 overflow-hidden">
                      <img
                        src={info.image}
                        alt={info.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-3">
                  <div className="flex items-start space-x-3">
                    <span className="flex-shrink-0 mt-1">{info.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 mb-1">{info.title}</div>
                      <div className="text-xs text-gray-500 mb-2">Комиссия {service.commission}%</div>
                      <div className="text-sm text-gray-600 leading-relaxed">{info.description}</div>
                    </div>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Additional info for selected service type */}
          {formData.service_type !== "general" && serviceTypes[formData.service_type] && (
            <div className="mt-4 p-3 rounded-lg" style={{background:'var(--rv-white)', border:'1px solid var(--rv-border)'}}>
              <div className="flex items-start space-x-2">
                <Lightbulb size={18} style={{color:'var(--rv-teal)'}} className="flex-shrink-0 mt-0.5" />
                <div className="text-sm" style={{color:'var(--rv-text-secondary)'}}>
                  <div className="font-medium mb-1">Особенности выбранной услуги:</div>
                  <div>{serviceTypes[formData.service_type].description}</div>
                </div>
              </div>
            </div>
          )}

          {/* Верификация личности исполнителя: обязательна для Мемориального сервиса (доступ к месту захоронения, деликатная задача) */}
          {formData.service_type === "grave_care" && (
            <div className="mt-3 flex items-start gap-2 text-sm p-3 rounded-lg" data-testid="mandatory-identity-verification-notice-grave-care" style={{background:'#EFF6FF', border:'1px solid #93C5FD'}}>
              <Lightbulb size={16} style={{color:'#2563EB'}} className="flex-shrink-0 mt-0.5" />
              <div style={{color:'#1E3A8A'}}>
                <span className="font-medium">Только для проверенных исполнителей: </span>
                Мемориальный сервис — деликатное задание, поэтому отклик доступен только исполнителям с подтверждённой личностью (документальная верификация).
              </div>
            </div>
          )}
        </div>

        {/* Общие услуги (Уровень 2): подкатегории, пресеты, AI-помощник */}
        {formData.service_type === "general" && (
          <div className="rounded-xl p-4 space-y-4" data-testid="general-subcategory-block" style={{background:'var(--rv-paper)', border:'1px solid var(--rv-border)'}}>
            <div>
              <button
                type="button"
                data-testid="subcategory-collapse-toggle"
                onClick={() => setSubcatCollapsed(v => !v)}
                className="w-full flex items-center justify-between gap-2"
              >
                <span className="text-sm font-semibold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
                  Что нужно сделать?
                </span>
                <span className="flex items-center gap-2 min-w-0">
                  {subcatCollapsed && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium truncate" data-testid="selected-subcategory-chip" style={{backgroundColor:'var(--rv-teal)', color:'#fff'}}>
                      {getSubcategory(formData.task_subcategory)?.label || "Не выбрано"}
                    </span>
                  )}
                  <ChevronDown size={18} className="flex-shrink-0" style={{color:'var(--rv-text-muted)', transform: subcatCollapsed ? 'none' : 'rotate(180deg)', transition:'transform 0.2s'}} />
                </span>
              </button>
              {!subcatCollapsed && (
                <>
                  <p className="text-xs mt-2 mb-3" style={{color:'var(--rv-text-muted)'}}>
                    Выберите тип поручения — от фото локации до бытового ремонта, поиска информации или составления документа.
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="subcategory-pills">
                    {GENERAL_SUBCATEGORIES.map((sub) => {
                      const active = formData.task_subcategory === sub.value;
                      return (
                        <button
                          key={sub.value}
                          type="button"
                          data-testid={`subcategory-${sub.value}`}
                          onClick={() => selectSubcategory(sub.value)}
                          className="px-3 py-1.5 rounded-full text-sm font-medium transition-all"
                          style={active
                            ? { backgroundColor: 'var(--rv-teal)', color: '#fff', border: '1px solid var(--rv-teal)' }
                            : { backgroundColor: '#fff', color: 'var(--rv-text-secondary)', border: '1px solid var(--rv-border)' }
                          }
                        >
                          {sub.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Что получит заказчик в результате */}
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{background:'#fff', border:'1px solid var(--rv-border)'}}>
              <Lightbulb size={16} style={{color:'var(--rv-teal)'}} className="flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium" style={{color:'var(--rv-navy)'}}>Результат: </span>
                <span data-testid="result-type-info" style={{color:'var(--rv-text-secondary)'}}>{RESULT_TYPE_INFO[formData.result_type] || RESULT_TYPE_INFO.media}</span>
                {!formData.location_required && (
                  <span className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-medium" data-testid="remote-task-badge" style={{background:'#EFF6FF', color:'#2563EB'}}>
                    Удалённое задание — адрес не требуется
                  </span>
                )}
              </div>
            </div>

            {/* Верификация личности исполнителя: обязательна для подкатегорий с доступом в дом/на участок/к личным вещам заказчика */}
            {getSubcategory(formData.task_subcategory)?.requires_identity_verification ? (
              <div className="flex items-start gap-2 text-sm p-3 rounded-lg" data-testid="mandatory-identity-verification-notice" style={{background:'#EFF6FF', border:'1px solid #93C5FD'}}>
                <Lightbulb size={16} style={{color:'#2563EB'}} className="flex-shrink-0 mt-0.5" />
                <div style={{color:'#1E3A8A'}}>
                  <span className="font-medium">Только для проверенных исполнителей: </span>
                  Такие задания связаны с доступом в дом, на участок, к личным вещам или посылкам, поэтому отклик доступен только исполнителям с подтверждённой личностью (документальная верификация).
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="verified_executors_only"
                  data-testid="verified-executors-only-checkbox"
                  checked={formData.verified_executors_only || false}
                  onChange={(e) => setFormData({ ...formData, verified_executors_only: e.target.checked })}
                  className="rounded border-gray-300 focus:ring-2"
                  style={{'accentColor': 'var(--rv-teal)'}}
                />
                <label htmlFor="verified_executors_only" className="text-sm" style={{color:'var(--rv-text-secondary)'}}>
                  Только для исполнителей с подтверждённой личностью
                </label>
              </div>
            )}

            {/* Быстрые шаблоны (сворачиваемый блок) */}
            <div className="rounded-lg" style={{background:'#fff', border:'1px solid var(--rv-border)'}}>
              <button
                type="button"
                data-testid="presets-collapse-toggle"
                onClick={() => setPresetsExpanded(v => !v)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5"
              >
                <span className="text-sm font-medium" style={{color:'var(--rv-text-secondary)'}}>
                  Быстрый старт по шаблону
                  <span className="ml-2 text-xs" style={{color:'var(--rv-text-muted)'}}>({GENERAL_PRESETS.length} шаблонов)</span>
                </span>
                <ChevronDown size={18} className="flex-shrink-0" style={{color:'var(--rv-text-muted)', transform: presetsExpanded ? 'rotate(180deg)' : 'none', transition:'transform 0.2s'}} />
              </button>
              {presetsExpanded && (
                <div className="flex flex-wrap gap-2 px-3 pb-3" data-testid="preset-pills">
                  {GENERAL_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      data-testid={`preset-${preset.id}`}
                      onClick={() => applyPreset(preset)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                      style={{ backgroundColor: '#fff', color: 'var(--rv-teal)', border: '1px dashed var(--rv-teal)' }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

        {/* AI-помощник формулировки — доступен во всех типах услуг */}
        {aiHelperEnabled && (
          <div>
            <button
              type="button"
              data-testid="ai-helper-toggle"
              onClick={() => setShowAiHelper(v => !v)}
              className="rv-ai-helper-btn w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold"
              style={{
                color: '#fff',
                background: 'linear-gradient(135deg, var(--rv-teal) 0%, var(--rv-teal-dark) 100%)',
                border: '1.5px solid var(--rv-teal)',
                cursor: 'pointer',
                fontFamily: 'Manrope, sans-serif'
              }}
            >
              <Sparkles size={18} className="rv-ai-spark" />
              {showAiHelper ? "Скрыть AI-помощник" : "Сформулировать задание с помощью AI"}
            </button>
            {showAiHelper && (
              <div className="mt-2 p-3 rounded-lg" style={{background:'#fff', border:'1px solid var(--rv-border)'}}>
                <p className="text-xs mb-2" style={{color:'var(--rv-text-muted)'}}>
                  Напишите задание своими словами — AI составит понятное название и описание. Отредактируйте под себя.
                </p>
                <textarea
                  rows="2"
                  data-testid="ai-prompt-input"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={AI_PLACEHOLDERS[formData.service_type] || AI_PLACEHOLDERS.general}
                />
                <button
                  type="button"
                  data-testid="ai-generate-btn"
                  onClick={runAiSuggest}
                  disabled={aiLoading}
                  className="reversia-btn reversia-btn-sm mt-2 justify-center"
                  style={{ background: 'var(--rv-teal)', color: '#fff' }}
                >
                  {aiLoading ? "Генерируем..." : "Сформулировать"}
                </button>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
            Название задания
          </label>
          <input
            type="text"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
            value={formData.title}
            data-testid="task-title-input"
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={TITLE_PLACEHOLDERS[formData.service_type] || TITLE_PLACEHOLDERS.general}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
            Описание
          </label>
          <textarea
            required
            rows="4"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder={formData.service_type === "general" ? (getSubcategory(formData.task_subcategory)?.placeholder || "Опишите детали задания: что нужно сделать, на что обратить внимание, как принять результат...") : (DESC_PLACEHOLDERS[formData.service_type] || "Опишите детали задания: что нужно сфотографировать, какие ракурсы, время съемки...")}
          />
        </div>

        {/* Фотографии к заданию для Общих услуг — помогают исполнителю оценить задание на этапе обсуждения */}
        {formData.service_type === "general" && (
          <div className="space-y-3 p-4 rounded-lg" data-testid="general-reference-media-block" style={{background:'#EFF6FF', border:'1px solid #BFDBFE'}}>
            <div>
              <h4 className="font-medium flex items-center gap-2" style={{color:'#1D4ED8'}}>
                <Upload size={16} />
                Фотографии к заданию (по желанию)
              </h4>
              <p className="text-xs mt-1" style={{color:'#2563EB'}}>
                Прикрепите до 5 фото — например, что нужно отремонтировать, куда доставить или как выглядит объект. Это поможет исполнителю на этапе обсуждения точнее оценить стоимость и решить, сможет ли он взяться за задание.
              </p>
            </div>
            <div>
              <input
                id="reference-media-input"
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleReferenceMediaUpload}
                disabled={uploadingFiles}
                className="hidden"
              />
              <button
                type="button"
                data-testid="general-reference-upload-btn"
                onClick={() => document.getElementById('reference-media-input').click()}
                disabled={uploadingFiles || formData.reference_media.length >= 5}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
                style={{background: (uploadingFiles || formData.reference_media.length >= 5) ? undefined : 'var(--rv-teal)'}}
              >
                <Upload size={16} />
                {uploadingFiles
                  ? 'Загрузка...'
                  : formData.reference_media.length >= 5
                    ? 'Максимум 5 фото загружено'
                    : `Выбрать фото (${formData.reference_media.length}/5)`
                }
              </button>
              <p className="text-xs text-gray-500 mt-1 text-center">JPG, PNG, MP4, MOV — не более 5 МБ каждый</p>
            </div>

            {formData.reference_media && formData.reference_media.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Прикреплённые файлы:</p>
                <div className="grid grid-cols-3 gap-2" data-testid="general-reference-preview">
                  {formData.reference_media.map((media, index) => (
                    <div key={index} className="relative rounded overflow-hidden border border-blue-200">
                      {media.startsWith('data:image') ? (
                        <img src={media} alt={`Фото ${index + 1}`} className="w-full h-20 object-cover" />
                      ) : (
                        <video src={media} className="w-full h-20 object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeReferenceMedia(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                      >
                        <XIcon size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {!formData.location_required && (
          <div className="p-3 rounded-lg flex items-center gap-2 text-sm" data-testid="remote-no-address" style={{background:'#EFF6FF', border:'1px solid #BFDBFE', color:'#1D4ED8'}}>
            <Globe size={16} className="flex-shrink-0" />
            Удалённое задание — указывать адрес и точку на карте не нужно.
          </div>
        )}

        {formData.location_required && (
        <div>
          <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
            Адрес/Локация
          </label>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <input
              type="text"
              required
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Москва, Парк Горького"
            />
            <div className="flex space-x-2 sm:space-x-2">
              <button
                type="button"
                onClick={geocodeAddress}
                disabled={locationLoading}
                className={`px-3 py-2 rounded-md transition-colors ${
                  locationLoading 
                    ? 'bg-blue-600 text-white cursor-not-allowed' 
                    : 'reversia-btn'
                }`}
              >
                {locationLoading ? (
                  <div className="flex items-center space-x-1">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <MapPin size={14} />
                  </div>
                ) : (
                  <MapPin size={16} />
                )}
              </button>
              <button
                type="button"
                onClick={getCurrentUserLocation}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-md transition-colors"
                title="Определить мое местоположение"
              >
                <Globe size={16} />
              </button>
              <button
                type="button"
                onClick={() => setShowMap(!showMap)}
                className="reversia-btn reversia-btn-sm" style={{background:'var(--rv-navy)', color:'white'}}
              >
                {showMap ? "Скрыть" : "Карта"}
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Нажмите "булавку" чтобы показать введённый адрес или координаты на карте. Нажмите "планету" для определения вашего текущего местоположения. Или кликните на карту для точного выбора точки.
          </p>
        </div>
        )}

        {formData.location_required && showMap && (() => {
          const mapProps = {
            latitude: formData.latitude,
            longitude: formData.longitude,
            onLocationSelect: (lat, lng, address) => {
              setFormData(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                location: address !== null ? address : `Координаты: ${lat.toFixed(4)}, ${lng.toFixed(4)}`
              }));
              if (showToast && address !== null) {
                showToast("Адрес обновлён по выбранной точке", "success");
              }
            }
          };
          return (
            <>
              {/* Fullscreen overlay через Portal — полностью вне transform-контекста формы */}
              {mapFullscreen && createPortal(
                <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,zIndex:9999,background:'#fff',display:'flex',flexDirection:'column'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 16px',borderBottom:'1px solid #e5e7eb',background:'#f9fafb',flexShrink:0}}>
                    <span style={{fontWeight:600,fontSize:'14px',color:'#374151'}}>Выбор местоположения</span>
                    <button type="button" onClick={() => setMapFullscreen(false)}
                      style={{background:'#6b7280',color:'#fff',border:'none',borderRadius:'6px',padding:'6px 14px',cursor:'pointer',fontSize:'13px'}}>
                      Свернуть
                    </button>
                  </div>
                  {typeof formData.latitude === 'number' && (
                    <CreateTaskMap {...mapProps} mapHeight="calc(100dvh - 60px)" />
                  )}
                </div>,
                document.body
              )}

              {/* Нормальный блок — всегда в DOM, скрыт только при fullscreen */}
              <div style={{display: mapFullscreen ? 'none' : 'block', background:'#f9fafb', padding:'16px', borderRadius:'8px'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'8px'}}>
                  <h3 className="text-sm font-medium text-gray-700">Кликните на карте для выбора местоположения</h3>
                  <button type="button" onClick={() => setMapFullscreen(true)}
                    style={{background:'#6b7280',color:'#fff',border:'none',borderRadius:'6px',padding:'4px 12px',cursor:'pointer',fontSize:'12px',flexShrink:0,marginLeft:'8px'}}>
                    На весь экран
                  </button>
                </div>
                {typeof formData.latitude === 'number' ? (
                  <CreateTaskMap {...mapProps} mapHeight="256px" />
                ) : (
                  <div className="text-center py-4 text-gray-500">Загрузка карты...</div>
                )}
              </div>
            </>
          );
        })()}

        {/* Additional fields for mobile journalist */}
        {formData.service_type === "mobile_journalist" && (
          <div className="space-y-4 p-4 rounded-xl border" style={{background: 'rgba(32,178,170,0.06)', borderColor: 'rgba(32,178,170,0.3)'}}>
            <h4 className="font-semibold text-sm" style={{color: 'var(--reversia-teal)'}}>Настройки для журналистского задания</h4>

            {/* Тип контента — пилюли */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Что нужно снять
              </label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: "photo", label: "Фото", icon: "camera" },
                  { value: "video", label: "Видео", icon: "video" },
                  { value: "photo_video", label: "Фото и видео", icon: "both" },
                ].map(({ value, label, icon }) => {
                  const active = (formData.media_content_type || "photo_video") === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFormData({ ...formData, media_content_type: value })}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-150"
                      style={active
                        ? { backgroundColor: 'var(--reversia-teal)', color: '#fff', boxShadow: '0 2px 8px rgba(32,178,170,0.35)' }
                        : { backgroundColor: '#fff', color: '#6b7280', border: '1.5px solid #d1d5db' }
                      }
                    >
                      {icon === "camera" && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>}
                      {icon === "video" && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>}
                      {icon === "both" && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/><polygon points="23 7 16 12 23 17 23 7" transform="translate(-8 0)"/></svg>}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Уровень срочности */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
                Уровень срочности
              </label>
              <select
                value={formData.urgency_level || "low"}
                onChange={(e) => setFormData({ ...formData, urgency_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
                style={{'--tw-ring-color': 'var(--reversia-teal)'}}
              >
                <option value="low">Низкий</option>
                <option value="medium">Средний</option>
                <option value="high">Высокий</option>
                <option value="urgent">Срочный</option>
              </select>
            </div>

            {/* Только верифицированные */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="verified_only"
                checked={formData.verified_journalists_only || false}
                onChange={(e) => setFormData({ ...formData, verified_journalists_only: e.target.checked })}
                className="rounded border-gray-300 focus:ring-2"
                style={{'accentColor': 'var(--reversia-teal)'}}
              />
              <label htmlFor="verified_only" className="text-sm text-gray-700">
                Только верифицированные журналисты
              </label>
            </div>

            <div className="text-xs" style={{color: 'rgba(32,178,170,0.75)'}}>
              <p>Требования: Фото 12МП+, Видео 4K+, геотеги и временные метки</p>
              <p>Комиссия платформы: 25%</p>
            </div>
          </div>
        )}

        {/* Пользовательские пункты проверки для тайного покупателя */}
        {formData.service_type === "secret_shopper" && (
          <div className="space-y-3 p-4 rounded-xl" style={{background:'#EFF6FF', border:'1px solid #BFDBFE'}}>
            <div>
              <h4 className="font-medium" style={{color:'#1D4ED8', fontFamily:'Manrope,sans-serif'}}>Пункты проверки (тайный покупатель)</h4>
              <p className="text-xs text-blue-600 mt-1">
                По умолчанию исполнитель оценит: качество обслуживания, поведение персонала, обстановку заведения.
                Добавьте свои специфические пункты для проверки.
              </p>
            </div>
            <div className="text-xs text-blue-700">
              <p>• Качество обслуживания (1-5)</p>
              <p>• Поведение персонала</p>
              <p>• Обстановка заведения</p>
              <p>• Обязательное фото-подтверждение</p>
            </div>
            {shopperCustomFields.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-700">Ваши пункты:</p>
                {shopperCustomFields.map((field, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-white rounded px-2 py-1 border border-blue-100">
                    <span className="text-sm flex-1">{field}</span>
                    <button
                      type="button"
                      onClick={() => setShopperCustomFields(prev => prev.filter((_, i) => i !== idx))}
                      className="text-red-400 hover:text-red-600 text-xs"
                    >
                      Удалить
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <input
                type="text"
                value={newShopperField}
                onChange={(e) => setNewShopperField(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (newShopperField.trim() && shopperCustomFields.length < 10) {
                      setShopperCustomFields(prev => [...prev, newShopperField.trim()]);
                      setNewShopperField('');
                    }
                  }
                }}
                placeholder="Например: Наличие меню на русском языке"
                className="flex-1 min-w-0 px-3 py-2 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
              />
              <button
                type="button"
                onClick={() => {
                  if (newShopperField.trim() && shopperCustomFields.length < 10) {
                    setShopperCustomFields(prev => [...prev, newShopperField.trim()]);
                    setNewShopperField('');
                  }
                }}
                disabled={!newShopperField.trim() || shopperCustomFields.length >= 10}
                className="reversia-btn reversia-btn-sm whitespace-nowrap" style={{background:'var(--rv-teal)', color:'white'}}
              >
                + Добавить
              </button>
            </div>
            {shopperCustomFields.length >= 10 && (
              <p className="text-xs text-orange-600">Максимум 10 дополнительных пунктов</p>
            )}
            <p className="text-xs text-blue-500">• Комиссия платформы: 15%</p>
          </div>
        )}

        {/* Reference media upload for grave_care */}
        {formData.service_type === "grave_care" && (
          <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <div>
              <h4 className="font-medium text-green-800 flex items-center gap-2">
                <Upload size={16} />
                Фото/видео подсказки для поиска участка
              </h4>
              <p className="text-xs text-green-700 mt-1">
                Загрузите до 3 фото или видео, которые помогут исполнителю найти нужное место на кладбище (вид с входа, соседние ориентиры, номер участка и т.д.)
              </p>
            </div>

            {/* Single upload button */}
            <div>
              <input
                id="reference-media-input"
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={handleReferenceMediaUpload}
                disabled={uploadingFiles}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => document.getElementById('reference-media-input').click()}
                disabled={uploadingFiles || formData.reference_media.length >= 3}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                <Upload size={16} />
                {uploadingFiles
                  ? 'Загрузка...'
                  : formData.reference_media.length >= 3
                    ? 'Максимум 3 файла загружено'
                    : `Выбрать файлы (${formData.reference_media.length}/3)`
                }
              </button>
              <p className="text-xs text-gray-500 mt-1 text-center">JPG, PNG, MP4, MOV — не более 5 МБ каждый</p>
            </div>

            {/* Uploaded files preview */}
            {formData.reference_media && formData.reference_media.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-700">Загруженные файлы:</p>
                <div className="grid grid-cols-3 gap-2">
                  {formData.reference_media.map((media, index) => (
                    <div key={index} className="relative rounded overflow-hidden border border-green-200">
                      {media.startsWith('data:image') ? (
                        <img
                          src={media}
                          alt={`Подсказка ${index + 1}`}
                          className="w-full h-20 object-cover"
                        />
                      ) : (
                        <video
                          src={media}
                          className="w-full h-20 object-cover"
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => removeReferenceMedia(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                      >
                        <XIcon size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Чеклист работ для заказчика — мемориальный сервис */}
        {formData.service_type === "grave_care" && (
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setWorksCollapsed(!worksCollapsed)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div>
                <span className="font-medium text-gray-800 text-sm">Что нужно сделать на участке</span>
                <span className="ml-2 text-xs text-gray-500">(отметьте нужное)</span>
                {selectedWorks.length > 0 && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full text-white" style={{backgroundColor: 'var(--reversia-teal)'}}>
                    выбрано: {selectedWorks.length}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-500">{worksCollapsed ? "Развернуть" : "Свернуть"}</span>
            </button>
            {!worksCollapsed && (
              <div className="p-4 space-y-2 bg-white">
                <p className="text-xs text-gray-500 mb-3">
                  Отметьте виды работ, которые требуются на участке. Это поможет исполнителю точно понять объём задания.
                </p>
                {GRAVE_CARE_WORKS.map((work) => (
                  <label key={work.id} className="flex items-start gap-2 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedWorks.includes(work.id)}
                      onChange={(e) => {
                        setSelectedWorks(prev =>
                          e.target.checked
                            ? [...prev, work.id]
                            : prev.filter(id => id !== work.id)
                        );
                      }}
                      className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">{work.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {formData.location_required && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
              Широта
            </label>
            <input
              type="number"
              step="any"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
              value={formData.latitude}
              onChange={(e) => setFormData({ ...formData, latitude: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
              Долгота
            </label>
            <input
              type="number"
              step="any"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
              value={formData.longitude}
              onChange={(e) => setFormData({ ...formData, longitude: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </div>
        )}

        <B2BCreateSection formData={formData} setFormData={setFormData} userEmail={user?.email} />
        <EvidenceReportToggle formData={formData} setFormData={setFormData} />

        <div>
          <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
            Цена (₽)
          </label>
          <input
            type="number"
            required
            min={formData.is_b2b ? 100 : MIN_PAID_TASK_PRICE}
            max="50000"
            step="50"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
            placeholder="500"
            data-testid="create-task-price-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Цена: от {formData.is_b2b ? 100 : MIN_PAID_TASK_PRICE} до 50 000 ₽
          </p>
          <p data-testid="refund-guarantee-price" className="text-xs mt-1.5 flex items-start gap-1.5" style={{color:'var(--rv-teal)'}}>
            <RotateCcw size={13} className="mt-0.5 flex-shrink-0" />
            <span>{formData.is_b2b
              ? "Оплата не проходит через платформу. Эту сумму организация платит исполнителю напрямую по его реквизитам после подтверждения выполнения."
              : "Эта сумма замораживается, а не списывается в пользу исполнителя. Отмените задание до начала работы — вернём 100% на карту (3-5 рабочих дней)."}</span>
          </p>

          {/* Аудит #35: прозрачный расчёт комиссии и выплаты (только для оплаты через платформу) */}
          {!formData.is_b2b && commissionPreview && (
            <div
              data-testid="commission-preview-box"
              className="mt-3 rounded-md border border-emerald-200 bg-gradient-to-br from-emerald-50/80 to-white px-3 py-2.5"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <span className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                  Прозрачный расчёт
                </span>
                {commissionPreview.promotion_active && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                    Акция активна
                  </span>
                )}
              </div>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-1 text-xs sm:text-sm">
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-gray-500">Цена задания</span>
                  <span className="font-semibold text-gray-800">{commissionPreview.price.toLocaleString('ru-RU')} ₽</span>
                </div>
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-gray-500">Комиссия {commissionPreview.commission_percent}%</span>
                  <span className="font-semibold text-gray-700">−{commissionPreview.commission_amount.toLocaleString('ru-RU')} ₽</span>
                </div>
                <div className="flex justify-between sm:flex-col sm:justify-start">
                  <span className="text-gray-500">Получит исполнитель</span>
                  <span className="font-semibold text-emerald-700">{commissionPreview.executor_payout.toLocaleString('ru-RU')} ₽</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1" style={{color:'var(--rv-text-primary)'}}>
            Срок выполнения
          </label>
          <input
            type="date"
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--rv-teal,#0d9488)]"
            value={formData.deadline ? formData.deadline.slice(0, 10) : ''}
            onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
            min={new Date().toISOString().slice(0, 10)}
          />
        </div>

        <p data-testid="refund-guarantee-submit" className="text-xs text-center" style={{color:'var(--rv-text-muted)'}}>
          {formData.is_b2b
            ? "Задание от организации публикуется без оплаты через платформу. Расчёт с исполнителем — напрямую, документы формируются после подтверждения."
            : "Публикация ни к чему не обязывает: задание можно отменить с полным возвратом, пока исполнитель не приступил к работе."}
        </p>

        <button
          type="submit"
          disabled={loading || (!paidTasksEnabled && !isAdmin)}
          data-testid="create-task-submit"
          className="reversia-btn reversia-btn-primary reversia-btn-lg w-full justify-center"
        >
          {(!paidTasksEnabled && !isAdmin) ? "Публикация заданий скоро откроется" : (loading ? "Создание..." : "Создать задание")}
        </button>
      </form>
    </div>
  );
};
